import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputMask } from 'primereact/inputmask';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';

import { differenceInCalendarDays, format, subDays } from 'date-fns';
import styled from 'styled-components';

import { useToast } from '../../../hooks/toast';
import { useTimer } from '../../../hooks/timer';
import api from '../../../services/api';
import {
  buscarResponsavelLogado,
  mensagemDeErroDaApi,
  ResponsavelLogado,
} from '../../../services/responsavelLogado';
import {
  descricaoSemDetalhe,
  hhmmDeMinutos,
  hhmmDoCronometro,
  minutosDeHHMM,
  montarDescricaoDoProcesso,
} from '../../../utils/horaDoProcesso';

export interface ProcessoParaHora {
  proc_cod_cliente: number;
  proc_num_pasta: string;
  proc_reg: string;
  proc_autor: string;
  proc_reu: string;
}

interface Props {
  visible: boolean;
  onHide(): void;
  onSalvo(): void;
  processo: ProcessoParaHora;
  origem: string;
  clienteNome: string;
}

type Modo = 'total' | 'horario';

const modos = [
  { label: 'Total de horas', value: 'total' },
  { label: 'Horário (De/Até)', value: 'horario' },
];

const Conteudo = styled.div`
  font-size: 13px;

  label {
    display: block;
    margin-bottom: 4px;
    font-weight: 600;
  }

  small {
    color: #6c757d;
  }

  .hora-resumo {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 8px 16px;
    padding: 10px 12px;
    margin-bottom: 12px;
    background: #f4f6f9;
    border-radius: 4px;

    span {
      display: block;
      font-size: 11px;
      color: #6c757d;
      text-transform: uppercase;
    }

    strong {
      font-weight: 600;
    }
  }

  .hora-resumo-largo {
    grid-column: 1 / -1;
  }

  .hora-cronometro {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    margin-bottom: 12px;
    border: 1px solid #b3d7f5;
    background: #eef6fd;
    border-radius: 4px;

    > span {
      flex: 1;
    }
  }

  .p-selectbutton .p-button {
    padding: 0.45rem 0.75rem;
    font-size: 13px;
  }

  @media (max-width: 576px) {
    .hora-resumo {
      grid-template-columns: 1fr;
    }
  }
`;

const formatarCronometro = (segundos: number) => {
  const h = String(Math.floor(segundos / 3600)).padStart(2, '0');
  const m = String(Math.floor(segundos / 60) % 60).padStart(2, '0');
  const s = String(segundos % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const HoraNoProcessoDialog: React.FC<Props> = ({
  visible,
  onHide,
  onSalvo,
  processo,
  origem,
  clienteNome,
}) => {
  const { addToast } = useToast();
  const timer = useTimer();
  const descricaoRef = useRef<HTMLTextAreaElement>(null);

  const [responsavel, setResponsavel] = useState<ResponsavelLogado | null>(
    null,
  );
  const [referencias, setReferencias] = useState<
    { name: string; value: number }[]
  >([]);

  const [data, setData] = useState<Date>(new Date());
  const [modo, setModo] = useState<Modo>('horario');
  const [total, setTotal] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [referencia, setReferencia] = useState<number | null>(null);
  const [descricao, setDescricao] = useState('');
  const [usouCronometro, setUsouCronometro] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  const hoje = new Date();
  const diasAtras = differenceInCalendarDays(hoje, data);

  useEffect(() => {
    if (!visible) return;

    setData(new Date());
    setModo('horario');
    setTotal('');
    setDe(format(new Date(), 'HH:mm'));
    setAte('');
    setReferencia(null);
    setDescricao(montarDescricaoDoProcesso(processo, origem));
    setUsouCronometro(false);
    setErro('');

    if (!responsavel) {
      buscarResponsavelLogado()
        .then(setResponsavel)
        .catch(() => setResponsavel(null));
    }

    if (referencias.length === 0) {
      api.get('lawsuits/references/show/all').then((response) => {
        setReferencias(
          response.data.map(
            (r: {
              horas_referencia_id: number;
              horas_referencia_nome: string;
            }) => ({
              name: r.horas_referencia_nome,
              value: r.horas_referencia_id,
            }),
          ),
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const focarFimDaDescricao = () => {
    const campo = descricaoRef.current;
    if (!campo) return;
    campo.focus();
    campo.setSelectionRange(campo.value.length, campo.value.length);
    campo.scrollTop = campo.scrollHeight;
  };

  const usarCronometro = () => {
    setModo('total');
    setTotal(hhmmDoCronometro(timer.elapsedSeconds));
    setUsouCronometro(true);
    setErro('');
  };

  const horaAberta = modo === 'horario' && !ate;

  const minutosInformados = () => {
    if (modo === 'total') return minutosDeHHMM(total);
    if (horaAberta) return 0;
    return minutosDeHHMM(ate) - minutosDeHHMM(de);
  };

  const validar = (): string => {
    if (!responsavel) {
      return 'Não foi possível identificar o seu cadastro de responsável. Use o formulário completo de horas.';
    }
    if (diasAtras > 30 || diasAtras < 0) {
      return 'A data precisa estar entre hoje e 30 dias atrás.';
    }
    if (modo === 'horario') {
      if (minutosDeHHMM(de) === 0 && de !== '00:00') {
        return 'Informe a hora de início (De).';
      }
      if (!horaAberta && minutosInformados() <= 0) {
        return 'A hora final precisa ser maior que a hora de início.';
      }
    } else if (minutosInformados() <= 0) {
      return 'Informe o total de horas trabalhadas.';
    }
    if (descricaoSemDetalhe(descricao)) {
      return 'Descreva o trabalho feito no fim da descrição, depois de "Descrição:".';
    }
    return '';
  };

  const salvar = async () => {
    const problema = validar();
    setErro(problema);
    if (problema) {
      if (problema.startsWith('Descreva')) focarFimDaDescricao();
      return;
    }

    setEnviando(true);
    try {
      await api.post('/management/workinghours/create', {
        horas_cod_cliente: processo.proc_cod_cliente,
        horas_cod_referencia: referencia ?? 0,
        horas_cod_responsavel: responsavel?.id,
        tipo_usuario: responsavel?.tipo,
        horas_descricao: descricao,
        horas_data: format(data, 'dd/MM/yyyy'),
        tipo_hora: modo === 'horario' ? '1' : '2',
        horas_hora_inicial_texto: modo === 'horario' ? de : '',
        horas_hora_final_texto: modo === 'horario' && !horaAberta ? ate : '',
        horas_hora_total:
          modo === 'total'
            ? hhmmDeMinutos(minutosDeHHMM(total))
            : hhmmDeMinutos(minutosInformados()),
        horas_tipo: 'processo',
        horas_reg: processo.proc_reg,
        horas_num_pasta: processo.proc_num_pasta,
        horas_autor: processo.proc_autor,
        horas_reu: processo.proc_reu,
      });

      if (usouCronometro) timer.reset();
      onSalvo();

      let resumo = `${hhmmDeMinutos(minutosInformados())} registradas na pasta ${processo.proc_num_pasta}.`;
      if (diasAtras > 7) {
        resumo =
          'A data tem mais de 7 dias: a hora foi registrada e está sujeita a análise.';
      }
      if (horaAberta) {
        resumo = `Início às ${de} na pasta ${processo.proc_num_pasta}. Conclua depois em Horas › Listar horas pendentes de finalização.`;
      }

      addToast({
        type: 'success',
        title: horaAberta ? 'Hora iniciada' : 'Hora cadastrada',
        description: resumo,
      });
      onHide();
    } catch (error) {
      setErro(mensagemDeErroDaApi(error));
    } finally {
      setEnviando(false);
    }
  };

  const rodape = (
    <div>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        className="p-button-text"
        type="button"
        onClick={onHide}
        disabled={enviando}
      />
      <Button
        label="Salvar hora"
        icon="pi pi-check"
        type="button"
        onClick={salvar}
        loading={enviando}
      />
    </div>
  );

  return (
    <Dialog
      header="Cadastrar hora neste processo"
      visible={visible}
      onHide={onHide}
      onShow={focarFimDaDescricao}
      footer={rodape}
      style={{ width: '640px' }}
      breakpoints={{ '700px': '95vw' }}
      modal
      blockScroll
      className="hora-no-processo-dialog"
    >
      <Conteudo>
        <div className="hora-resumo">
          <div>
            <span>Pasta</span>
            <strong>{processo.proc_num_pasta}</strong>
          </div>
          <div>
            <span>Processo</span>
            <strong>{processo.proc_reg || '—'}</strong>
          </div>
          {clienteNome ? (
            <div className="hora-resumo-largo">
              <span>Cliente</span>
              <strong>{clienteNome}</strong>
            </div>
          ) : null}
          <div className="hora-resumo-largo">
            <span>Responsável</span>
            <strong>{responsavel?.nome ?? 'carregando…'}</strong>
          </div>
        </div>

        {timer.elapsedSeconds > 0 ? (
          <div className="hora-cronometro">
            <i className="pi pi-stopwatch" />
            <span>
              Cronômetro em{' '}
              <strong>{formatarCronometro(timer.elapsedSeconds)}</strong>
            </span>
            <Button
              label={usouCronometro ? 'Tempo aplicado' : 'Usar este tempo'}
              icon={usouCronometro ? 'pi pi-check' : 'pi pi-arrow-down'}
              className="p-button-sm p-button-outlined"
              type="button"
              onClick={usarCronometro}
            />
          </div>
        ) : null}

        <div className="formgrid grid">
          <div className="field col-12 sm:col-5">
            <label htmlFor="hora-processo-data">Data do trabalho</label>
            <Calendar
              inputId="hora-processo-data"
              value={data}
              onChange={(e) => {
                if (e.value instanceof Date) setData(e.value);
              }}
              minDate={subDays(hoje, 30)}
              maxDate={hoje}
              dateFormat="dd/mm/yy"
              readOnlyInput
              showIcon
              className="w-full"
            />
          </div>
          <div className="field col-12 sm:col-7">
            <label htmlFor="hora-processo-modo">Tempo trabalhado</label>
            <SelectButton
              id="hora-processo-modo"
              value={modo}
              options={modos}
              onChange={(e) => {
                if (e.value) setModo(e.value);
              }}
              unselectable={false}
            />
          </div>

          {modo === 'total' ? (
            <div className="field col-6 sm:col-4">
              <label htmlFor="hora-processo-total">Total (hh:mm)</label>
              <InputMask
                id="hora-processo-total"
                mask="99:99"
                placeholder="00:00"
                autoClear={false}
                value={total}
                onChange={(e) => {
                  setTotal(e.value ?? '');
                  setUsouCronometro(false);
                }}
                className="w-full"
              />
            </div>
          ) : (
            <>
              <div className="field col-6 sm:col-4">
                <label htmlFor="hora-processo-de">De</label>
                <InputMask
                  id="hora-processo-de"
                  mask="99:99"
                  placeholder="00:00"
                  autoClear={false}
                  value={de}
                  onChange={(e) =>
                    setDe(e.value?.replace(/[_:]/g, '') ? e.value : '')
                  }
                  className="w-full"
                />
              </div>
              <div className="field col-6 sm:col-4">
                <label htmlFor="hora-processo-ate">Até</label>
                <InputMask
                  id="hora-processo-ate"
                  mask="99:99"
                  placeholder="00:00"
                  autoClear={false}
                  value={ate}
                  onChange={(e) =>
                    setAte(e.value?.replace(/[_:]/g, '') ? e.value : '')
                  }
                  className="w-full"
                />
              </div>
            </>
          )}

          <div className="field col-12">
            <label htmlFor="hora-processo-referencia">Referência</label>
            <Dropdown
              inputId="hora-processo-referencia"
              value={referencia}
              options={referencias}
              optionLabel="name"
              optionValue="value"
              onChange={(e) => setReferencia(e.value)}
              filter
              filterBy="name"
              showClear
              placeholder="Opcional"
              emptyFilterMessage="Nenhum registro encontrado"
              className="w-full"
            />
          </div>

          <div className="field col-12">
            <label htmlFor="hora-processo-descricao">Descrição</label>
            <InputTextarea
              ref={descricaoRef}
              id="hora-processo-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={6}
              className="w-full"
            />
            <small>Complete depois de &quot;Descrição:&quot;.</small>
          </div>
        </div>

        {horaAberta ? (
          <Message
            severity="info"
            text="Sem o horário final, a hora fica aberta: conclua depois em Horas › Listar horas pendentes de finalização."
            className="w-full mb-2"
          />
        ) : null}
        {diasAtras > 7 && diasAtras <= 30 ? (
          <Message
            severity="info"
            text="Data com mais de 7 dias: a hora fica sujeita a análise."
            className="w-full mb-2"
          />
        ) : null}
        {erro ? (
          <Message severity="error" text={erro} className="w-full" />
        ) : null}
      </Conteudo>
    </Dialog>
  );
};

export default HoraNoProcessoDialog;
